import React, { ReactNode } from 'react'

import { twMerge } from '../../tailwind/merge'
import { Box } from '../box'
import { Scrollable } from '../scrollable'
import { PanelHeader, PanelHeaderVariant } from './PanelHeader'

export enum PanelWithHeaderVariant {
    Small = 300,
    Decreased = 328,
    Std = 360,
    Medium = 420,
    Large = 654,
}

const variantWidth: Record<PanelWithHeaderVariant, string> = {
    [PanelWithHeaderVariant.Small]: 'w-[300px]',
    [PanelWithHeaderVariant.Decreased]: 'w-[328px]',
    [PanelWithHeaderVariant.Std]: 'w-[360px]',
    [PanelWithHeaderVariant.Medium]: 'w-[420px]',
    [PanelWithHeaderVariant.Large]: 'w-[654px]',
}

export interface PanelWithHeaderProps {
    header?: ReactNode
    variant?: PanelWithHeaderVariant
    headerVariant?: PanelHeaderVariant
    disableScroll?: boolean
    splitterRightPane?: boolean
    headerClassName?: string
    className?: string
    seleniumLocation?: string
    children?: ReactNode
}

/**
 * Panel header over a body that fills the rest of the height. `className` styles the body.
 * Unless `disableScroll`, the body scrolls on both axes and its children see a definite
 * full-height box, as inside the legacy `DarkScrollbar`.
 */
export const PanelWithHeader = ({
    header,
    variant,
    headerVariant = 'standard',
    disableScroll,
    splitterRightPane,
    headerClassName,
    className,
    seleniumLocation,
    children,
}: PanelWithHeaderProps) => (
    <Box
        className={twMerge(
            'h-full',
            variant ? variantWidth[variant] : 'w-full shrink',
            splitterRightPane && '-ml-px',
        )}
        data-test-id={seleniumLocation || 'panel-with-header'}
    >
        <PanelHeader variant={headerVariant} className={twMerge('pl-4 pr-5', headerClassName)}>
            {header}
        </PanelHeader>
        <Box
            className={twMerge(
                'bg-background-canvas',
                disableScroll ? 'flex-auto' : 'flex-1',
                className,
            )}
        >
            {disableScroll ? (
                children
            ) : (
                <Scrollable className='absolute inset-0'>{children}</Scrollable>
            )}
        </Box>
    </Box>
)

PanelWithHeader.displayName = 'PanelWithHeader'
